import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import IconArrowDown from 'assets/Icons/Arrow/Icon_arrow_down';
import IconArrowUp from 'assets/Icons/Arrow/Icon_arrow_up';
import DropdownButton from 'components/Buttons/DropdownButton';
import { DropdownCell, DropdownLayout } from 'components/Dropdown/DropdownForms';
import { useGetHospitalListQueries } from 'api/Queries/CL/Hospital/useGetHospitalListQueries';
import { CL_ALL_HOSPITAL_ID, useGetUserListQueries } from 'api/Queries/CL/User/useGetUserListQueries';
import TodayDiagnosisFilter from 'pages/DCK/TodayDiagnosisPage/TodayDiagnosisFilter';
import useToggle from 'hooks/CustomHooks/useToggle';
import useQueryPaginationStateHooks from 'hooks/CustomHooks/useQueryPaginationStateHooks';
import { CLUserLookupSearchFilterList } from '../constants';
import { formatUserSearchFilter } from '../utils';
import { pagePaths } from 'constants/path';
import { DiagnosisInnerLayout, DiagnosisLayout, FilterWrapper, HospitalPageLabel } from 'styles/Global/hospitalStyles';
import { PageTitle } from 'styles/Global/commonStyles';
import { Body3Medium, Body5Regular } from 'styles/Global/typography';
import { BackgroundColor, BorderColor, TextColor, WarningColor } from 'styles/Global/colorSemantic';
import styled from 'styled-components';
import CLUserLookupPagination from './CLUserLookupPagination';
import { useLoginStore } from 'store/useLoginDataStore';

const CLUserLookupPage = () => {
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const navigate = useNavigate();
    const { loginData } = useLoginStore();
    const hospitalDropdownRef = useRef<HTMLDivElement>(null);

    const [selectedSearchFilter, setSelectedSearchFilter] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>(queryParams.get('search') || '');
    const [selectedHospitalId, setSelectedHospitalId] = useState<string>(queryParams.get('hospitalId') || CL_ALL_HOSPITAL_ID);
    const [sortField, setSortField] = useState<string>('registrationTimestamp');
    const [sortDirections, setSortDirections] = useState<Record<string, boolean>>({ registrationTimestamp: false });
    const [isSearchFilterDropdown, setIsSearchFilterDropdown] = useToggle();
    const [isHospitalDropdown, setIsHospitalDropdown] = useState(false);

    const { currentPage, pageGroup, setMultipleParams } = useQueryPaginationStateHooks();
    const { data: hospitalListData } = useGetHospitalListQueries();

    const {
        data: userListData,
        refetch: refetchUserListData,
        isLoading,
        isError,
        error,
    } = useGetUserListQueries(
        currentPage,
        pageGroup,
        formatUserSearchFilter(CLUserLookupSearchFilterList[selectedSearchFilter]),
        queryParams.get('search') || '',
        sortField,
        sortDirections[sortField] ? 'asc' : 'desc',
        null,
        selectedHospitalId
    );

    useEffect(() => {
        setSearchValue(queryParams.get('search') || '');
        setSelectedHospitalId(queryParams.get('hospitalId') || CL_ALL_HOSPITAL_ID);
    }, [search]);

    useEffect(() => {
        const handleClickOutside = (event: globalThis.MouseEvent) => {
            if (hospitalDropdownRef.current && !hospitalDropdownRef.current.contains(event.target as Node)) {
                setIsHospitalDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [search]);

    const handleDropdown = (e: MouseEvent, idx: number) => {
        e.stopPropagation();
        setIsSearchFilterDropdown();
        setSelectedSearchFilter(idx);
        setSearchValue('');
    };

    const handleSearchFilter = () => {
        setMultipleParams({
            page: 1,
            pageGroup: 0,
            search: searchValue,
            hospitalId: selectedHospitalId,
        });
        refetchUserListData();
    };

    const handleResetButton = () => {
        setSelectedHospitalId(CL_ALL_HOSPITAL_ID);
        navigate(`${pagePaths.CLAdminUserLookup}?hospitalId=${CL_ALL_HOSPITAL_ID}`, { replace: true });
    };

    const handlePageChange = (page: number, newGroup?: number) => {
        setMultipleParams(
            {
                page,
                pageGroup: newGroup !== undefined ? newGroup : Number(queryParams.get('pageGroup')) || 0,
                search: searchValue,
                hospitalId: selectedHospitalId,
            },
            page,
            newGroup !== undefined ? newGroup : Number(queryParams.get('pageGroup')) || 0
        );
    };

    const handleHospitalSelect = (e: MouseEvent, hospitalId: string) => {
        e.stopPropagation();
        setSelectedHospitalId(hospitalId);
        setIsHospitalDropdown(false);
        setMultipleParams(
            {
                page: 1,
                pageGroup: 0,
                search: searchValue,
                hospitalId,
            },
            1,
            0
        );
    };

    const handleSorting = (type: string) => {
        if (type !== 'registrationTimestamp') return;

        const direction = !sortDirections.registrationTimestamp;
        setSortField('registrationTimestamp');
        setSortDirections({ registrationTimestamp: direction });
    };

    const selectedHospitalName = selectedHospitalId === CL_ALL_HOSPITAL_ID
        ? '전체 병원'
        : hospitalListData?.find((hospital) => hospital._id === selectedHospitalId)?.name
            || loginData.hospitalName
            || '병원 선택';
    const userList = userListData?.list || [];
    const pagination = userListData?.pagination;
    const isInitialLoading = isLoading && !userListData;
    const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다';

    return (
        <DiagnosisLayout>
            <DiagnosisInnerLayout>
                <HospitalPageLabel onClick={() => window.location.reload()}>사용자 조회</HospitalPageLabel>
                <PageTitle $marginbottom='24px'>사용자 조회</PageTitle>
                <HospitalFilterRow>
                    <FilterLabel>병원</FilterLabel>
                    <HospitalDropdownWrapper ref={hospitalDropdownRef}>
                        <DropdownButton $width='280px' onClick={() => setIsHospitalDropdown((prev) => !prev)}>
                            <HospitalName>{selectedHospitalName}</HospitalName>
                            {isHospitalDropdown ? <IconArrowUp /> : <IconArrowDown />}
                        </DropdownButton>
                        {isHospitalDropdown && (
                            <DropdownLayout $position='absolute' $height='240px' $padding='4px 0' $top='44px' $left='0' $width='280px'>
                                <DropdownCell
                                    $height='36px'
                                    $padding='8px 12px'
                                    onClick={(e: MouseEvent) => handleHospitalSelect(e, CL_ALL_HOSPITAL_ID)}
                                    $selected={selectedHospitalId === CL_ALL_HOSPITAL_ID}
                                >
                                    전체 병원
                                </DropdownCell>
                                {hospitalListData?.map((hospital) => (
                                    <DropdownCell
                                        key={hospital._id}
                                        $height='36px'
                                        $padding='8px 12px'
                                        onClick={(e: MouseEvent) => handleHospitalSelect(e, hospital._id)}
                                        $selected={selectedHospitalId === hospital._id}
                                    >
                                        {hospital.name || hospital._id}
                                    </DropdownCell>
                                ))}
                            </DropdownLayout>
                        )}
                    </HospitalDropdownWrapper>
                </HospitalFilterRow>
                <FilterWrapper>
                    <TodayDiagnosisFilter
                        type='clUserLookup'
                        diagnosisNumber={pagination?.totalCount}
                        selectedSearchFilter={selectedSearchFilter}
                        handleDropdown={handleDropdown}
                        isSearchFilterDropdown={isSearchFilterDropdown}
                        setIsSearchFilterDropdown={setIsSearchFilterDropdown}
                        onChange={setSearchValue}
                        searchValue={searchValue}
                        handleSearchFilter={handleSearchFilter}
                    />
                </FilterWrapper>
                {isError && (
                    <ErrorNotice>
                        사용자 목록을 불러오지 못했습니다. {errorMessage}
                    </ErrorNotice>
                )}
                {isInitialLoading ? (
                    <LoadingNotice>사용자 목록을 불러오는 중입니다.</LoadingNotice>
                ) : (
                    <CLUserLookupPagination
                        initNumber={userList.length}
                        currentPage={currentPage}
                        totalPages={pagination?.totalPages || 0}
                        pageGroup={pageGroup}
                        handlePageChange={handlePageChange}
                        currentData={userList}
                        handleSorting={handleSorting}
                        sortField={sortField}
                        sortDirections={sortDirections}
                        handleResetButton={handleResetButton}
                    />
                )}
            </DiagnosisInnerLayout>
        </DiagnosisLayout>
    );
};

export default CLUserLookupPage;

const HospitalFilterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    height: 40px;
    margin-bottom: 12px;
`;

const FilterLabel = styled(Body3Medium)`
    color: ${TextColor.color_text_light};
`;

const HospitalDropdownWrapper = styled.div`
    position: relative;
`;

const HospitalName = styled.span`
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ErrorNotice = styled(Body5Regular)`
    width: 100%;
    padding: 12px 14px;
    margin-bottom: 12px;
    color: ${WarningColor.color_warning_normal};
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    background-color: ${BackgroundColor.color_background_warning};
`;

const LoadingNotice = styled(Body5Regular)`
    width: 100%;
    padding: 40px 0;
    color: ${TextColor.color_text_light};
    text-align: center;
`;
